import React, { useMemo, useRef, useState, useEffect, useCallback } from "react";
import ChartBurgerMenu from "./ChartBurgerMenu";
import BoltLoader from "./BoltLoader";
import FieldHelp from "../help/FieldHelp";

const getLightShade = (hex) => {
  if (!hex || !hex.startsWith("#") || hex.length !== 7) return "#fff";
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const intensity = 0.15;
  const lightR = Math.round(255 * (1 - intensity) + r * intensity);
  const lightG = Math.round(255 * (1 - intensity) + g * intensity);
  const lightB = Math.round(255 * (1 - intensity) + b * intensity);
  return `rgb(${lightR},${lightG},${lightB})`;
};

const cleanMetricLabel = (label) => {
  if (!label) return "";
  const beforeMatch = label.split("_ [")[0].split("_[")[0].trim();
  if (beforeMatch && beforeMatch !== label) return beforeMatch;
  return label
    .replace(/\[.*?\]/g, "")
    .replace(/pf/gi, "")
    .trim();
};

const HeatmapChart = ({
  rows = [],
  timeLabels = [],
  minValue = null,
  maxValue = null,
  loading = false,
  metricLabel = "",
  valueFormatter = (value) =>
    value == null || Number.isNaN(value)
      ? "–"
      : `${value.toFixed?.(1) ?? value}`,
  machineColor = "#ef4444", // default red
  rowHeaderLabel = "Day",
  metricType = "",
  // Optional FieldHelp id for the title; without one the title renders as-is.
  titleHelpId,
}) => {
  const chartRef = useRef(null);
  const wrapperRef = useRef(null);
  const tooltipRef = useRef(null);

  const range = useMemo(() => {
    if (metricType === "temperature") {
      if (minValue == null || maxValue == null) {
        return { min: 0, max: 60, spread: 60 };
      }
      const spread = maxValue - minValue || 1;
      return { min: minValue, max: maxValue, spread };
    }
    // Humidity uses a single-hue light->dark ramp over the real data range,
    // so the gradient legend (min -> max) matches what the cells show.
    if (metricType === "humidity") {
      if (minValue == null || maxValue == null) {
        return { min: 0, max: 100, spread: 100 };
      }
      const spread = maxValue - minValue || 1;
      return { min: minValue, max: maxValue, spread };
    }
    if (minValue == null || maxValue == null) {
      return { min: 0, max: 0, spread: 0 };
    }
    const spread = maxValue - minValue || 1;
    return { min: minValue, max: maxValue, spread };
  }, [minValue, maxValue, metricType]);

  const machineRgb = useMemo(() => {
    if (machineColor && machineColor.startsWith("#") && machineColor.length === 7) {
      return [
        parseInt(machineColor.slice(1, 3), 16),
        parseInt(machineColor.slice(3, 5), 16),
        parseInt(machineColor.slice(5, 7), 16),
      ];
    }
    return null;
  }, [machineColor]);

  // Use the provided machineColor for the heatmap gradient
  const getColor = useCallback((value) => {
    if (value == null || Number.isNaN(value)) {
      return {
        background: "#f8fafc",
        color: "#94a3b8",
      };
    }
    const intensity = (value - range.min) / range.spread;
    const clamped = Math.min(Math.max(intensity, 0), 1);

    if (machineColor === "multi") {
      if (metricType === "temperature") {
        // One colour, intensity only: the hotter the hour, the darker the cell.
        const light = [254, 226, 226]; // #fee2e2
        const dark = [127, 29, 29]; // #7f1d1d
        const r = Math.round(light[0] + (dark[0] - light[0]) * clamped);
        const g = Math.round(light[1] + (dark[1] - light[1]) * clamped);
        const b = Math.round(light[2] + (dark[2] - light[2]) * clamped);
        return {
          background: `rgb(${r},${g},${b})`,
          color: clamped > 0.55 ? "#ffffff" : "#0f172a",
        };
      }
      if (metricType === "humidity") {
        // One colour, intensity only: the wetter the hour, the darker the cell.
        const light = [219, 234, 254]; // #dbeafe
        const dark = [23, 37, 84]; // #172554
        const r = Math.round(light[0] + (dark[0] - light[0]) * clamped);
        const g = Math.round(light[1] + (dark[1] - light[1]) * clamped);
        const b = Math.round(light[2] + (dark[2] - light[2]) * clamped);
        return {
          background: `rgb(${r},${g},${b})`,
          color: clamped > 0.55 ? "#ffffff" : "#0f172a",
        };
      }

      // Default continuous fallback using clamped desaturated scale
      let r, g, b;
      if (clamped < 0.33) {
        const ratio = clamped / 0.33;
        r = Math.round(94 + (214 - 94) * ratio);
        g = Math.round(159 + (166 - 159) * ratio);
        b = Math.round(135 + (92 - 135) * ratio);
      } else if (clamped < 0.66) {
        const ratio = (clamped - 0.33) / 0.33;
        r = Math.round(214 + (200 - 214) * ratio);
        g = Math.round(166 + (122 - 166) * ratio);
        b = Math.round(92 + (75 - 92) * ratio);
      } else {
        const ratio = (clamped - 0.66) / 0.34;
        r = Math.round(200 + (181 - 200) * ratio);
        g = Math.round(122 + (91 - 122) * ratio);
        b = Math.round(75 + (91 - 75) * ratio);
      }
      return {
        background: `rgb(${r},${g},${b})`,
        color: clamped > 0.5 ? "#ffffff" : "#0f172a",
      };
    }

    // If machineColor is a hex, interpolate with white for intensity
    // If hex, blend with white for background, and use full color at max
    if (machineRgb) {
      // At max value, use pure color
      if (clamped === 1) {
        return {
          background: `rgb(${machineRgb[0]},${machineRgb[1]},${machineRgb[2]})`,
          color: "#fff",
        };
      }
      const mappedIntensity = 0.15 + 0.85 * clamped;
      const r = Math.round(255 * (1 - mappedIntensity) + machineRgb[0] * mappedIntensity);
      const g = Math.round(255 * (1 - mappedIntensity) + machineRgb[1] * mappedIntensity);
      const b = Math.round(255 * (1 - mappedIntensity) + machineRgb[2] * mappedIntensity);
      return {
        background: `rgb(${r},${g},${b})`,
        color: mappedIntensity > 0.55 ? "#fff" : "#7f1d1d",
      };
    }
    // If not hex, fallback to hsl red gradient
    const hue = 5;
    const saturation = 80 + clamped * 15;
    const lightness = 90 - clamped * 50;
    return {
      background: `hsl(${hue}, ${saturation}%, ${lightness}%)`,
      color: clamped > 0.55 ? "#fff" : "#7f1d1d",
    };
  }, [range, machineColor, metricType, machineRgb]);

  const cleanedMetricLabel = useMemo(() => cleanMetricLabel(metricLabel), [metricLabel]);

  const handleCellEnter = useCallback((event, formattedValue, timestampLabel, cleanedMetric) => {
    if (!wrapperRef.current || !tooltipRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    tooltipRef.current.style.display = 'block';
    tooltipRef.current.style.top = `${event.clientY - rect.top + 12}px`;
    tooltipRef.current.style.left = `${event.clientX - rect.left + 12}px`;

    const valElem = tooltipRef.current.querySelector('.tooltip-value');
    const labelElem = tooltipRef.current.querySelector('.tooltip-label');
    if (valElem) valElem.textContent = `${cleanedMetric} ${formattedValue}`;
    if (labelElem) labelElem.textContent = timestampLabel;
  }, []);

  const handleCellMove = useCallback((event) => {
    if (!wrapperRef.current || !tooltipRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    tooltipRef.current.style.top = `${event.clientY - rect.top + 12}px`;
    tooltipRef.current.style.left = `${event.clientX - rect.left + 12}px`;
  }, []);

  const handleCellLeave = useCallback(() => {
    if (tooltipRef.current) {
      tooltipRef.current.style.display = 'none';
    }
  }, []);
  const gridCells = useMemo(() => {
    return (
      <div style={unifiedGridStyle(timeLabels.length)}>
        {/* Top-left corner cell */}
        <div style={columnHeaderStyle}>{rowHeaderLabel}</div>

        {/* Top hourly headers */}
        {timeLabels.map((label) => (
          <div key={label} style={gridHeaderCellStyle}>
            {label}
          </div>
        ))}

        {/* Rows */}
        {rows.map((row) => (
          <React.Fragment key={row.label}>
            {/* Day Label Cell */}
            <div style={dayLabelCellStyle}>{row.label}</div>

            {/* Heatmap Cells */}
            {row.values.map((value, index) => {
              const { background, color } = getColor(value);
              const timeLabel = timeLabels[index];
              const timestampLabel = `${row.label} ${timeLabel}`;
              const formattedValue = valueFormatter(value);
              return (
                <div
                  key={`${row.label}-${timeLabel}`}
                  style={{
                    ...gridCellStyle,
                    background,
                    color,
                  }}
                  onMouseEnter={(event) =>
                    handleCellEnter(event, formattedValue, timestampLabel, cleanedMetricLabel)
                  }
                  onMouseMove={handleCellMove}
                  onMouseLeave={handleCellLeave}
                >
                  <span style={visuallyHidden}>{formattedValue}</span>
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    );
  }, [timeLabels, rowHeaderLabel, rows, getColor, valueFormatter, cleanedMetricLabel, handleCellEnter, handleCellMove, handleCellLeave]);


  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (loading) {
      setIsReady(false);
    } else if (rows.length > 0 && timeLabels.length > 0) {
      const timer = setTimeout(() => setIsReady(true), 50);
      return () => clearTimeout(timer);
    } else {
      setIsReady(true);
    }
  }, [loading, rows, timeLabels]);

  if (loading || (!isReady && rows.length > 0)) {
    return (
      <div style={{ position: "relative", width: "100%", minHeight: 400, background: "#fff", borderRadius: 16 }}>
        <BoltLoader label="Generating heatmap…" />
      </div>
    );
  }

  if (!rows.length || !timeLabels.length) {
    return (
      <div style={emptyStateStyle}>
        No heatmap data yet. Try refreshing or expanding the date range.
      </div>
    );
  }

  return (
    <div
      ref={chartRef}
      style={{
        background: "#fff",
        borderRadius: 16,
        padding: 20,
        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        {/* Machine name inside card, top left */}
        <div style={{
          fontWeight: 700,
          fontSize: 18,
          color: "#0f172a",
          marginRight: 24,
          minWidth: 120,
        }}>
          <FieldHelp id={titleHelpId}>{cleanedMetricLabel}</FieldHelp>
        </div>
        {/* Legend and burger menu grouped right */}
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          {minValue != null && maxValue != null && (
            <div style={legendGradientStyle}>
              <span style={legendLabelStyle}>{valueFormatter(minValue)}</span>
              <div style={{
                ...legendBarStyle,
                background:
                  machineColor === "multi"
                    ? metricType === "humidity"
                      // single-hue ramps: the legend has to match the cells
                      ? "linear-gradient(90deg, #dbeafe, #172554)"
                      : metricType === "temperature"
                        ? "linear-gradient(90deg, #fee2e2, #7f1d1d)"
                        : "linear-gradient(90deg, #10b981, #f59e0b, #f97316, #ef4444)"
                    : machineColor.startsWith("#") && machineColor.length === 7
                      ? `linear-gradient(90deg, ${getLightShade(machineColor)}, ${machineColor})`
                      : legendBarStyle.background,
                border:
                  machineColor === "multi"
                    ? "1px solid #cbd5e1"
                    : machineColor.startsWith("#") && machineColor.length === 7
                      ? `1px solid ${machineColor}`
                      : legendBarStyle.border,
              }} />
              <span style={legendLabelStyle}>{valueFormatter(maxValue)}</span>
            </div>
          )}
          <ChartBurgerMenu
            chartRef={chartRef}
            chartTitle="Average Heatmap Weekday"
            exportContainer={true}
            chartData={{
              columns: timeLabels,
              rows: rows.map((r) => ({
                label: r.label,
                values: r.values.map((v) =>
                  v == null || Number.isNaN(v) ? 0 : v,
                ),
              })),
            }}
            chartType="heatmap"
          />
        </div>
      </div>

      <div style={wrapperStyle} ref={wrapperRef}>
        <div style={scrollWrapperStyle}>
          {gridCells}
        </div>
        <div
          ref={tooltipRef}
          style={{
            ...tooltipStyle,
            display: "none",
          }}
        >
          <div className="tooltip-value" style={tooltipValueStyle}></div>
          <div className="tooltip-label" style={tooltipLabelStyle}></div>
        </div>
      </div>
    </div>
  );
};

const wrapperStyle = {
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: 12,
  position: "relative",
};

const scrollWrapperStyle = {
  width: "100%",
  overflowX: "hidden",
  borderRadius: 12,
  padding: "4px 0",
};

const unifiedGridStyle = (count) => ({
  display: "grid",
  // 1 column for day label, 'count' columns for heatmap cells
  gridTemplateColumns: `minmax(80px, 100px) repeat(${count}, 1fr)`,
  gap: 2,
  width: "100%",
  boxSizing: "border-box",
});

const columnHeaderStyle = {
  background: "#0f172a",
  color: "#f8fafc",
  padding: "8px 10px",
  borderRadius: 12,
  textAlign: "center",
  fontWeight: 600,
};

const dayLabelCellStyle = {
  background: "#f1f5f9",
  borderRadius: 8,
  padding: "8px 10px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#0f172a",
  fontWeight: 700,
  fontSize: 12,
};

const gridHeaderCellStyle = {
  background: "#0f172a",
  color: "#f8fafc",
  padding: "6px 4px",
  textAlign: "center",
  fontSize: 10,
  borderRadius: 8,
  fontWeight: 600,
};

const gridCellStyle = {
  borderRadius: 6,
  height: 34,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 600,
  fontSize: 11,
  cursor: "pointer",
};

const visuallyHidden = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  border: 0,
};

const legendGradientStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
};

const legendBarStyle = {
  width: 180,
  height: 10,
  borderRadius: 999,
  background:
    "linear-gradient(90deg, hsl(5, 80%, 90%), hsl(5, 95%, 40%))",
  border: "1px solid #fecaca",
};

const legendLabelStyle = {
  fontSize: 12,
  color: "#475569",
};

const tooltipStyle = {
  position: "absolute",
  pointerEvents: "none",
  background: "rgba(15, 23, 42, 0.92)",
  color: "#fff",
  borderRadius: 8,
  padding: "8px 12px",
  boxShadow: "0 10px 25px rgba(15, 23, 42, 0.2)",
  transform: "translate(-50%, -120%)",
  whiteSpace: "nowrap",
  zIndex: 10,
  border: "1px solid rgba(248, 113, 113, 0.4)",
};

const tooltipValueStyle = {
  fontWeight: 700,
  fontSize: 14,
};

const tooltipLabelStyle = {
  fontSize: 11,
  color: "#e2e8f0",
};

// formatTooltipTimestamp removed as specific dates are no longer shown in heatmap tooltips

const emptyStateStyle = {
  padding: 32,
  textAlign: "center",
  color: "#94a3b8",
  border: "1px dashed #cbd5f5",
  borderRadius: 16,
};

export default React.memo(HeatmapChart);
